import {
  Users,
  Wallet,
  GraduationCap,
  TrendingUp,
  BookOpen,
  Briefcase,
  Heart,
  type LucideIcon,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — fixture data.
//
// Every screen in the demo reads from this file. There is no
// backend, no fetch, no async — the demo is deliberately a
// static fixture so that reviewers see the same UI every time
// and cannot accidentally conflate "the data is wrong" with
// "the UI is wrong".
//
// The types defined here are the canonical ones. Anything else
// that needs a `Student`, a `Payment`, or a `Role` imports from
// this file rather than redeclaring.
// ─────────────────────────────────────────────────────────────────

// ─── Roles ──────────────────────────────────────────────────────

export type Role = "director" | "bursar" | "teacher" | "student" | "parent";

export const ROLES: { key: Role; label: string; Icon: LucideIcon }[] = [
  { key: "director", label: "Director", Icon: Briefcase },
  { key: "bursar",   label: "Bursar",   Icon: Wallet },
  { key: "teacher",  label: "Teacher",  Icon: GraduationCap },
  { key: "student",  label: "Student",  Icon: BookOpen },
  { key: "parent",   label: "Parent",   Icon: Heart },
];

// ─── Routes ─────────────────────────────────────────────────────

export type RouteKey =
  | "dashboard"
  | "students"
  | "finance"
  | "academics"
  | "attendance"
  | "reports"
  | "analytics"
  | "users"
  | "settings"
  | "customize";

const ROLE_ROUTES: Record<Role, readonly RouteKey[]> = {
  director: ["dashboard", "analytics", "users", "students", "finance", "academics", "attendance", "reports", "settings", "customize"],
  bursar: ["dashboard", "students", "finance", "attendance", "reports", "settings"],
  teacher: ["dashboard", "students", "academics", "attendance", "reports", "settings"],
  student: ["dashboard", "academics", "attendance", "reports", "settings"],
  parent: ["dashboard", "academics", "attendance", "reports", "settings"],
};

export function canAccessRoute(role: Role | null, route: RouteKey): boolean {
  return role !== null && ROLE_ROUTES[role].includes(route);
}

// ─── Stats ──────────────────────────────────────────────────────

export type Stat = {
  label: string;
  value: string;
  caption?: string;
  delta?: number;
  icon: LucideIcon;
  tone: "blue" | "green" | "orange" | "red" | "purple" | "brand";
  spark?: number[];
};

export const directorStats: Stat[] = [
  {
    label: "Students",
    value: "412",
    caption: "Term 2, 2026",
    delta: 3.2,
    icon: Users,
    tone: "brand",
    spark: [380, 388, 392, 395, 401, 405, 408, 412],
  },
  {
    label: "Fees collected",
    value: "UGX 77M",
    caption: "72% of target",
    delta: 8.4,
    icon: Wallet,
    tone: "green",
    spark: [42, 48, 51, 58, 62, 68, 71, 77],
  },
  {
    label: "Staff",
    value: "38",
    caption: "31 teaching",
    delta: 0,
    icon: Briefcase,
    tone: "blue",
    spark: [34, 35, 35, 36, 37, 37, 38, 38],
  },
  {
    label: "Avg. score",
    value: "68%",
    caption: "Term 1 baseline",
    delta: -1.8,
    icon: TrendingUp,
    tone: "orange",
    spark: [72, 71, 70, 71, 69, 68, 68, 68],
  },
];

export const bursarStats: Stat[] = [
  {
    label: "Collected",
    value: "UGX 77M",
    caption: "Term 2, so far",
    delta: 8.4,
    icon: Wallet,
    tone: "green",
    spark: [42, 48, 51, 58, 62, 68, 71, 77],
  },
  {
    label: "Outstanding",
    value: "UGX 30M",
    caption: "94 students",
    delta: -4.1,
    icon: Users,
    tone: "red",
    spark: [38, 37, 35, 34, 32, 31, 30, 30],
  },
  {
    label: "Defaulters",
    value: "34",
    caption: "Over 30 days",
    delta: -12.5,
    icon: Briefcase,
    tone: "orange",
    spark: [48, 45, 42, 40, 38, 36, 35, 34],
  },
  {
    label: "Mobile money",
    value: "93%",
    caption: "MTN + Airtel",
    delta: 2.1,
    icon: TrendingUp,
    tone: "blue",
    spark: [88, 89, 90, 90, 91, 92, 92, 93],
  },
];

export const teacherStats: Stat[] = [
  {
    label: "Today's classes",
    value: "4",
    caption: "Next at 10:40",
    icon: BookOpen,
    tone: "brand",
  },
  {
    label: "Students",
    value: "168",
    caption: "Across 5 streams",
    icon: Users,
    tone: "blue",
    spark: [160, 162, 163, 165, 166, 167, 167, 168],
  },
  {
    label: "Pending marks",
    value: "23",
    caption: "S3 Maths mid-term",
    delta: -15.0,
    icon: GraduationCap,
    tone: "orange",
    spark: [42, 38, 35, 32, 30, 27, 25, 23],
  },
  {
    label: "Avg. score",
    value: "71%",
    caption: "Your 5 streams",
    delta: 2.4,
    icon: TrendingUp,
    tone: "green",
    spark: [66, 67, 68, 69, 70, 70, 71, 71],
  },
];

// ─── Students ───────────────────────────────────────────────────

export type Student = {
  id: string;
  name: string;
  klass: string;
  guardian: string;
  balance: string;
  attendance: number;
};

export const students: Student[] = [
  { id: "s1",  name: "Amina Nakato",       klass: "S3", guardian: "Grace Nakato",     balance: "UGX 0",       attendance: 96 },
  { id: "s2",  name: "Brian Okello",       klass: "S3", guardian: "Peter Okello",     balance: "UGX 320,000", attendance: 88 },
  { id: "s3",  name: "Catherine Auma",     klass: "S2", guardian: "Mary Auma",        balance: "UGX 0",       attendance: 94 },
  { id: "s4",  name: "David Mugisha",      klass: "S4", guardian: "Paul Mugisha",     balance: "UGX 150,000", attendance: 91 },
  { id: "s5",  name: "Esther Nambooze",    klass: "S1", guardian: "Ruth Nambooze",    balance: "UGX 0",       attendance: 98 },
  { id: "s6",  name: "Francis Kaggwa",     klass: "S3", guardian: "John Kaggwa",      balance: "UGX 480,000", attendance: 72 },
  { id: "s7",  name: "Grace Tumusiime",    klass: "S2", guardian: "Alice Tumusiime",  balance: "UGX 0",       attendance: 93 },
  { id: "s8",  name: "Henry Ssemakula",    klass: "S4", guardian: "Robert Ssemakula", balance: "UGX 220,000", attendance: 85 },
  { id: "s9",  name: "Irene Kirabo",       klass: "S1", guardian: "Susan Kirabo",     balance: "UGX 0",       attendance: 97 },
  { id: "s10", name: "Joel Wanyama",       klass: "S3", guardian: "Michael Wanyama",  balance: "UGX 90,000",  attendance: 89 },
  { id: "s11", name: "Kevin Ssali",        klass: "S2", guardian: "Betty Ssali",      balance: "UGX 0",       attendance: 92 },
  { id: "s12", name: "Lydia Atim",         klass: "S4", guardian: "Samuel Atim",      balance: "UGX 410,000", attendance: 68 },
];

// ─── Payments ───────────────────────────────────────────────────

export type PaymentStatus = "paid" | "pending" | "failed";

export type Payment = {
  id: string;
  student: string;
  guardian: string;
  amount: string;
  raw: number;
  balance: string;
  due: string;
  method: string;
  status: PaymentStatus;
  when: string;
};

export const payments: Payment[] = [
  { id: "p1", student: "Amina Nakato",    guardian: "Grace Nakato",    amount: "UGX 750,000", raw: 750000, balance: "UGX 0",       due: "05 Feb", method: "MTN MoMo",    status: "paid",    when: "2h ago" },
  { id: "p2", student: "Brian Okello",    guardian: "Peter Okello",    amount: "UGX 300,000", raw: 300000, balance: "UGX 320,000", due: "12 Feb", method: "Airtel Money", status: "pending", when: "5h ago" },
  { id: "p3", student: "Catherine Auma",  guardian: "Mary Auma",       amount: "UGX 750,000", raw: 750000, balance: "UGX 0",       due: "05 Feb", method: "Bank",         status: "paid",    when: "1d ago" },
  { id: "p4", student: "David Mugisha",   guardian: "Paul Mugisha",    amount: "UGX 200,000", raw: 200000, balance: "UGX 150,000", due: "12 Feb", method: "MTN MoMo",     status: "paid",    when: "1d ago" },
  { id: "p5", student: "Francis Kaggwa",  guardian: "John Kaggwa",     amount: "UGX 100,000", raw: 100000, balance: "UGX 480,000", due: "12 Feb", method: "Airtel Money", status: "failed",  when: "2d ago" },
  { id: "p6", student: "Grace Tumusiime", guardian: "Alice Tumusiime", amount: "UGX 750,000", raw: 750000, balance: "UGX 0",       due: "05 Feb", method: "MTN MoMo",     status: "paid",    when: "2d ago" },
  { id: "p7", student: "Henry Ssemakula", guardian: "Robert Ssemakula", amount: "UGX 250,000", raw: 250000, balance: "UGX 220,000", due: "12 Feb", method: "Bank",        status: "paid",    when: "3d ago" },
  { id: "p8", student: "Lydia Atim",      guardian: "Samuel Atim",     amount: "UGX 150,000", raw: 150000, balance: "UGX 410,000", due: "12 Feb", method: "MTN MoMo",     status: "pending", when: "4d ago" },
];

export const recentPayments = payments.filter((p) => p.status === "paid").slice(0, 6);

// ─── Fee breakdown ──────────────────────────────────────────────

export const feeBreakdown: { label: string; amount: number }[] = [
  { label: "Tuition",   amount: 480000 },
  { label: "Boarding",  amount: 220000 },
  { label: "Meals",     amount: 180000 },
  { label: "Transport", amount: 90000  },
  { label: "Activities", amount: 60000 },
];

// ─── Announcements ──────────────────────────────────────────────

export type Announcement = {
  id: string;
  title: string;
  body: string;
  when: string;
  urgent: boolean;
};

export const announcements: Announcement[] = [
  {
    id: "a1",
    title: "Term 2 fee deadline",
    body: "All outstanding balances must be cleared by 15 February. Kindly use MTN or Airtel mobile money.",
    when: "2d",
    urgent: true,
  },
  {
    id: "a2",
    title: "Parents' visiting day",
    body: "Saturday 22 February, 10:00 to 16:00. Refreshments will be served in the main hall.",
    when: "4d",
    urgent: false,
  },
  {
    id: "a3",
    title: "Inter-house athletics",
    body: "Trials begin next Wednesday. All S1–S4 students should report to the field by 15:00.",
    when: "1w",
    urgent: false,
  },
  {
    id: "a4",
    title: "Staff briefing",
    body: "All teaching staff are requested to attend the term review in the staff room on Friday at 16:30.",
    when: "1w",
    urgent: false,
  },
];

// ─── Upcoming events ────────────────────────────────────────────

export type UpcomingEvent = {
  id: string;
  title: string;
  where: string;
  when: string;
  tone: "blue" | "green" | "orange" | "red" | "purple" | "brand" | "neutral";
};

export const upcomingEvents: UpcomingEvent[] = [
  { id: "e1", title: "Parents' visiting day", where: "Main hall",     when: "Sat 22",  tone: "brand" },
  { id: "e2", title: "Mid-term exams",        where: "All classes",   when: "Mon 24",  tone: "orange" },
  { id: "e3", title: "Inter-house athletics", where: "Sports field",  when: "Wed 26",  tone: "blue" },
];

export const studentStats: Stat[] = [
  { label: "My average", value: "76%", caption: "Term 2, 2026", delta: 3.6, icon: TrendingUp, tone: "brand", spark: [68, 70, 71, 72, 73, 75, 76] },
  { label: "Attendance", value: "96%", caption: "12 of 12 classes", icon: Users, tone: "green", spark: [91, 92, 93, 94, 95, 95, 96] },
  { label: "Subjects", value: "8", caption: "This term", icon: BookOpen, tone: "blue" },
  { label: "Next exam", value: "6 days", caption: "Mathematics", icon: GraduationCap, tone: "orange" },
];

export const parentStats: Stat[] = [
  { label: "Children average", value: "74%", caption: "Term 2, 2026", delta: 2.8, icon: TrendingUp, tone: "brand", spark: [68, 69, 70, 71, 72, 73, 74] },
  { label: "Attendance", value: "93%", caption: "Across your children", icon: Users, tone: "green", spark: [88, 89, 90, 91, 92, 92, 93] },
  { label: "Outstanding", value: "UGX 90K", caption: "One child needs follow-up", icon: Wallet, tone: "orange" },
  { label: "Next exam", value: "6 days", caption: "Mathematics", icon: GraduationCap, tone: "blue" },
];